import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../lib/api';

interface User {
  id: number;
  full_name: string;
  email: string;
  role: string;
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { full_name: string; email: string; password: string; phone?: string; organisation_name?: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'esanza_buyer_token';
const USER_KEY = 'esanza_buyer_user';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem(USER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const u = data.user || data;
          setUser({ id: u.id, full_name: u.full_name, email: u.email, role: u.role, phone: u.phone });
          localStorage.setItem(USER_KEY, JSON.stringify({ id: u.id, full_name: u.full_name, email: u.email, role: u.role, phone: u.phone }));
        } else {
          // Token invalid - clear
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setToken(null);
          setUser(null);
        }
      } catch {
        // Network error - keep cached user
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || data.error || 'Login failed');

    const t = data.accessToken || data.token;
    const u = data.user;
    if (!t) throw new Error('No token received');

    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    if (u) {
      setUser(u);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
    }
  }, []);

  const register = useCallback(async (data: { full_name: string; email: string; password: string; phone?: string; organisation_name?: string }) => {
    const res = await fetch(`${API_URL}/api/auth/register-buyer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.message || result.error || 'Registration failed');

    const t = result.accessToken;
    const u = result.user;
    if (!t) throw new Error('No token received');

    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    if (u) {
      setUser(u);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user && !!token,
      login,
      register,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
